const API_BASE_URL = process.env.REACT_APP_API_URL || "";

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        // Check if this is a PIN requirement error
        if (response.status === 401 && data.requirePin) {
          // Return the data instead of throwing, so login can handle it
          return data;
        }
        throw new Error(data.error || `HTTP ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`API Error ${endpoint}:`, error);
      throw error;
    }
  }

  // Auth endpoints
  async signup(password, publicKey) {
    return this.request("/auth/signup", {
      method: "POST",
      body: JSON.stringify({ password, publicKey }),
    });
  }

async login(token, password, twoFACode = null, pinCode = null) {
  const body = { token, password };
  if (twoFACode) body.twoFACode = twoFACode;
  if (pinCode) body.pinCode = pinCode;

  console.log("🔵 [API] Login request:", {
    hasToken: !!token,
    hasPassword: !!password,
    hasTwoFACode: !!twoFACode,
    hasPinCode: !!pinCode
  });

  try {
    const response = await this.request("/auth/login", {
      method: "POST",
      body: JSON.stringify(body),
    });

    console.log("🟢 [API] Login response received:", {
      hasJwtToken: !!response.jwtToken,
      hasRefreshToken: !!response.refreshToken,
      hasUuid: !!response.uuid,
      hasSessionToken: !!response.sessionToken,
      hasPublicKey: !!response.publicKey,
      hasKeyVersion: !!response.keyVersion,
      twoFAEnabled: response.twoFAEnabled,
      pinEnabled: response.pinEnabled,
      publicKeyPreview: response.publicKey?.substring(0, 50)
    });

    // Validate that public key is present
    if (!response.publicKey) {
      console.warn("⚠️ [API] No public key in login response!");
    }

    // Return standardized response object
    return {
      jwtToken: response.jwtToken,
      refreshToken: response.refreshToken,
      uuid: response.uuid,
      sessionToken: response.sessionToken,
      keyVersion: response.keyVersion,
      twoFAEnabled: response.twoFAEnabled || false,
      pinEnabled: response.pinEnabled || false,
      recoveryCodesShown: response.recoveryCodesShown || false,
      publicKey: response.publicKey || null,
      require2FA: response.require2FA || false,
      requirePin: response.requirePin || false,
      message: response.message
    };
  } catch (error) {
    console.error("🔴 [API] Login error:", {
      message: error.message,
      status: error.status,
      requires2FA: error.require2FA,
      requiresPin: error.requirePin
    });
    
    // Re-throw with additional context
    throw error;
  }
}

  async logout(refreshToken) {
    return this.request("/auth/logout", {
      method: "POST",
      body: JSON.stringify({ refreshToken }),
    });
  }

  async refreshToken(refreshToken) {
    return this.request("/auth/refresh-token", {
      method: "POST",
      body: JSON.stringify({ refreshToken }),
    });
  }

  async getSessionToken() {
    return this.request("/auth/session-token");
  }

  // User endpoints
  async getUserByUuid(uuid) {
    return this.request(`/users/uuid/${uuid}`);
  }

  async rotateKeys(newPublicKey, currentPassword) {
    return this.request("/users/rotate-keys", {
      method: "POST",
      body: JSON.stringify({ newPublicKey, currentPassword }),
    });
  }

  // Message endpoints
  async getMessages(limit = 100) {
    return this.request(`/messages?limit=${limit}`);
  }

  async deleteMessage(messageId) {
    return this.request(`/messages/${messageId}`, {
      method: "DELETE",
    });
  }

  async markMessageDelivered(messageId) {
    return this.request(`/messages/${messageId}/delivered`, {
      method: "POST",
    });
  }

  async markMessageRead(messageId) {
    return this.request(`/messages/${messageId}/read`, {
      method: "POST",
    });
  }

  // Contact endpoints
  async getContacts(showArchived = false) {
    return this.request(`/contacts?showArchived=${showArchived}`);
  }

  async addContact(contactUuid, alias = null) {
    return this.request("/contacts", {
      method: "POST",
      body: JSON.stringify({ contactUuid, alias }),
    });
  }

  async updateContact(contactId, updates) {
    return this.request(`/contacts/${contactId}`, {
      method: "PUT",
      body: JSON.stringify(updates),
    });
  }

  async deleteContact(contactId) {
    return this.request(`/contacts/${contactId}`, {
      method: "DELETE",
    });
  }

  async archiveContact(contactId, archive) {
    return this.request(`/contacts/${contactId}/archive`, {
      method: "POST",
      body: JSON.stringify({ archive }),
    });
  }

  // Session endpoints
  async initSession(contactUuid) {
    return this.request("/sessions/init", {
      method: "POST",
      body: JSON.stringify({ contactUuid }),
    });
  }

  // 2FA endpoints
  async setup2FA() {
    return this.request("/2fa/setup", {
      method: "POST",
    });
  }

  async verify2FA(code) {
    return this.request("/2fa/verify", {
      method: "POST",
      body: JSON.stringify({ twoFACode: code }),
    });
  }

  async disable2FA(code) {
    return this.request("/2fa/disable", {
      method: "POST",
      body: JSON.stringify({ twoFACode: code }),
    });
  }

  // PIN endpoints
  async setPin(pinCode) {
    return this.request("/pin/set", {
      method: "POST",
      body: JSON.stringify({ pinCode }),
    });
  }

  async verifyPin(pinCode) {
    return this.request("/pin/verify", {
      method: "POST",
      body: JSON.stringify({ pinCode }),
    });
  }

  async disablePin() {
    return this.request("/pin/disable", {
      method: "POST",
    });
  }

  // Recovery endpoints
  async getRecoveryCodes() {
    return this.request("/recovery/codes");
  }

  async regenerateRecoveryCodes() {
    return this.request("/recovery/codes/regenerate", {
      method: "POST",
    });
  }

  async recoverWithCode(uuid, recoveryCode) {
    return this.request("/recovery/use-code", {
      method: "POST",
      body: JSON.stringify({ uuid, recoveryCode }),
    });
  }

  async recoverWithPassword(uuid, password) {
    return this.request("/recovery/new-token", {
      method: "POST",
      body: JSON.stringify({ uuid, password }),
    });
  }

  async verifyPin(pinCode) {
    return this.request("/pin/verify", {
      method: "POST",
      body: JSON.stringify({ pinCode }),
    });
  }

   // File endpoints
  async initializeFileUpload(recipientUuid, fileName, fileSize, mimeType, fileHash) {
    return this.request('/files/initialize', {
      method: 'POST',
      body: JSON.stringify({ recipientUuid, fileName, fileSize, mimeType, fileHash }),
    });
  }

  async getFileInfo(fileId) {
    return this.request(`/files/info/${fileId}`);
  }

  async deleteFile(fileId) {
    return this.request(`/files/${fileId}`, {
      method: 'DELETE',
    });
  }

}

export default new ApiService();
